// Tokens de UI del modulo de Proyectos.
//
// Centraliza decisiones visuales que estaban inline y dispersas para evitar
// drift entre vistas. Estetica objetivo: Linear/Notion minimal, paleta
// slate + cyan, tipografia tracking-tight.
//
// Como usarlo: importar las constantes/recetas necesarias y componerlas en
// className. Por ejemplo:
//   <button className={`${BUTTON.secondary} ${BUTTON.size.md}`}>
//   <div className={ROW.hover}>
//
// Si necesitas un valor que no esta aca: AGREGARLO aca antes de hardcodearlo
// en otra vista, asi mantenemos la fuente unica.

// ---------- Border radius (por jerarquia) ----------
// Cards "grandes" (proyectos, secciones, modales): rounded-xl
// Cards "estandar" (tarea, item de lista): rounded-lg
// Inputs, botones, dropdowns: rounded-md
// Badges, pills, chips: rounded-md (no rounded-sm)
export const RADIUS = {
  cardLg: 'rounded-xl',
  card:   'rounded-lg',
  input:  'rounded-md',
  badge:  'rounded-md',
  pill:   'rounded-full',
};

// ---------- Sombras ----------
// Dropdowns y popovers: shadow-lg + ring sutil
// Modales: shadow-2xl
// Cards en hover (lift): shadow-md (raro, usar con criterio)
export const SHADOW = {
  dropdown: 'shadow-xl ring-1 ring-slate-200',
  modal:    'shadow-2xl',
  cardHover:'shadow-md',
};

// ---------- Hover / Selected / Focus para filas y items ----------
// Receta unica para filas clickeables (lista, mis tareas, sidebars):
//   idle    -> sin bg
//   hover   -> bg-slate-50
//   selected-> bg-cyan-50 (NO usar slate como selected, eso colisiona con hover)
// Para chips/items mas chicos usar bg-slate-50/70 hover si se quiere mas sutil.
export const ROW = {
  // Estandar para filas clickeables en listas y tablas.
  hover:        'hover:bg-slate-50 transition-colors',
  // Mas sutil — usar SOLO dentro de visualizaciones (gantt, roadmap) donde el
  // hover compite con barras y bandas de fondo.
  hoverSubtle:  'hover:bg-slate-50/40 transition-colors',
  selected:     'bg-cyan-50',
  selectedSoft: 'bg-cyan-50/60',
};

// ---------- Focus rings ----------
// Inputs (todos: globales y modales): ring-2 cyan/20 + border cyan
// Botones: usar focus-visible:ring-2 cyan/40 (mas saturado en CTAs)
// Anillo nativo del navegador: SIEMPRE quitarlo con outline-none y reemplazar.
export const FOCUS = {
  input:  'focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500',
  button: 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/40',
};

// ---------- Botones ----------
// Tres variantes (primary, secondary, ghost) + dos tamaños (sm, md).
// Mantener gap-1.5 cuando hay icono + texto.
export const BUTTON = {
  primary:   'inline-flex items-center gap-1.5 bg-slate-900 text-white hover:bg-slate-800 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-md font-medium',
  secondary: 'inline-flex items-center gap-1.5 bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-md font-medium',
  ghost:     'inline-flex items-center gap-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-md font-medium',
  danger:    'inline-flex items-center gap-1.5 bg-rose-600 text-white hover:bg-rose-500 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-md font-medium',
  size: {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-sm',
  },
};

// ---------- Empty state (icon-box) ----------
// Tamano canonical del cuadro redondeado del empty state principal:
//   - w-14 h-14 con icono size=24 y blur cyan-100/40 detras.
// Para empty states "inline" mas chicos (sidebar, dropdown), usar Inbox suelto
// con size 16-20.
// Ver componente EmptyState para el uso recomendado.
export const EMPTY_ICON_BOX = 'w-14 h-14 rounded-2xl bg-white border border-slate-200/70 flex items-center justify-center mx-auto';
export const EMPTY_ICON_BLUR = 'absolute inset-0 bg-cyan-100/40 rounded-2xl blur-xl';

// ---------- Tipografia ----------
// Escala consistente para titulos/labels frecuentes.
// Usar tracking-tight en titulos, tracking-wide/wider en uppercase.
export const TEXT = {
  // Headers de seccion (en sidebar de gantt/roadmap, columnas board, etc)
  sectionLabel: 'text-[11px] font-bold tracking-[0.1em] uppercase text-slate-600',
  // Nombre principal de un item (tarea, proyecto)
  itemTitle:    'text-sm font-semibold text-slate-800 tracking-tight',
  // Subtitulo/metadata bajo un item
  itemMeta:     'text-[11px] text-slate-500',
  // Counter, tabular (e.g. "12 tareas")
  counter:      'text-[10px] font-medium text-slate-400 tabular-nums',
  // Helper text en forms
  formHint:     'text-xs text-slate-500',
};

// ---------- Layout ----------
export const LAYOUT = {
  // Sidebar de las vistas con eje temporal (gantt, roadmap).
  sidebarWidth: 280,
  // Sidebar del roadmap (un poquito mas ancho por el chip de health).
  sidebarWidthLg: 300,
  // Altura del header principal de shells.
  shellHeaderHeight: 56,
  // Altura del header secundario (sub-toolbar).
  subToolbarHeight: 64,
  // Padding lateral estandar del shell.
  shellPaddingX: 'px-6',
};
